// adminportal/src/pages/Monitoring.tsx
import React, { useState, useEffect, useRef } from 'react';
import Card from '../components/Card';
import Button from '../components/Button';
import { apiUrl, transferMonitoringApiUrl } from '../config';
import { RefreshCw, Wifi, WifiOff, AlertTriangle, Activity, Server, Shield, XCircle, UserX, AlertCircle } from 'lucide-react';
import SftpMonitoringChart from '../components/SftpMonitoringChart';
import Copyright from '../components/Copyright';

interface FailedLogin {
  userId: string;
  username: string;
  timestamp: string;
  ipAddress: string;
  reason?: string;
}

interface MonitoringData {
  failedLogins?: FailedLogin[];
  sftpMetrics?: {
    serverId: string;
    period: string;
    lastUpdated: string;
    note?: string;
    metrics: {
      BytesIn: {
        description: string;
        unit: string;
        datapoints: Array<{
          timestamp: string;
          sum: number;
          average: number;
          maximum: number;
        }>;
        summary: {
          total: number;
          average: number;
          max: number;
        };
      };
      BytesOut: {
        description: string;
        unit: string;
        datapoints: Array<{
          timestamp: string;
          sum: number;
          average: number;
          maximum: number;
        }>;
        summary: {
          total: number;
          average: number;
          max: number;
        };
      };
      FilesIn: {
        description: string;
        unit: string;
        datapoints: Array<{
          timestamp: string;
          sum: number;
          average: number;
          maximum: number;
        }>;
        summary: {
          total: number;
          average: number;
          max: number;
        };
      };
      FilesOut: {
        description: string;
        unit: string;
        datapoints: Array<{
          timestamp: string;
          sum: number;
          average: number;
          maximum: number;
        }>;
        summary: {
          total: number;
          average: number;
          max: number;
        };
      };
    };
  };
  errorCounts: {
    failedLogins: number;
    incompleteTransfers: number;
    s3Errors: number;
    apiErrors: number;
    lastUpdated: string;
  };
  securityEvents: Array<{
    id: string;
    type: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    description: string;
    timestamp: string;
    userId?: string;
    ipAddress?: string;
    resolved: boolean;
  }>;
}

export default function Monitoring() {
  const [monitoringData, setMonitoringData] = useState<MonitoringData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [notifications, setNotifications] = useState<
    Array<{ id: string; message: string; type: 'info' | 'success' | 'warning' | 'error' }>
  >([]);
  const wsRef = useRef<WebSocket | null>(null);
  const intervalRef = useRef<number | null>(null);

  const addNotification = (
    message: string,
    type: 'info' | 'success' | 'warning' | 'error' = 'info'
  ) => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const fetchMonitoringData = async (showNotification = false) => {
    try {
      setError(null);

      // Fetch data from available monitoring endpoints
      const [dashboardResponse, sftpResponse, errorsResponse, securityResponse] =
        await Promise.allSettled([
          fetch(apiUrl('dashboard-data')),
          fetch(apiUrl('sftp-metrics')),
          fetch(apiUrl('error-counts')),
          fetch(apiUrl('security-events')),
        ]);

      const [dashboardData, sftpData, errorsData, securityData] = [
        dashboardResponse.status === 'fulfilled' && dashboardResponse.value.ok
          ? await dashboardResponse.value.json()
          : {},
        sftpResponse.status === 'fulfilled' && sftpResponse.value.ok
          ? await sftpResponse.value.json()
          : null,
        errorsResponse.status === 'fulfilled' && errorsResponse.value.ok
          ? await errorsResponse.value.json()
          : null,
        securityResponse.status === 'fulfilled' && securityResponse.value.ok
          ? await securityResponse.value.json()
          : null,
      ];

      // Use real data from APIs

      // Combine all monitoring data with real data
      const combinedData: MonitoringData = {
        failedLogins: dashboardData.failedLogins || [],
        sftpMetrics: sftpData || dashboardData.sftpMetrics,
        errorCounts: errorsData ||
          dashboardData.errorCounts || {
            failedLogins: 0,
            incompleteTransfers: 0,
            s3Errors: 0,
            apiErrors: 0,
            lastUpdated: new Date().toISOString(),
          },
        securityEvents: securityData || [],
      };

      setMonitoringData(combinedData);
      setLastRefresh(new Date());
      if (showNotification) {
        addNotification('Monitoring data refreshed', 'success');
      }
    } catch (err: any) {
      console.error('Error fetching monitoring data:', err);
      setError(err.message);
      addNotification('Failed to refresh monitoring data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const setupWebSocket = () => {
    try {
      const wsUrl = apiUrl('cloudwatch-metrics-ws')
        .replace('https://', 'wss://')
        .replace('http://', 'ws://');

      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('🔌 WebSocket connected');
        setWsConnected(true);
        addNotification('WebSocket connected - real-time monitoring active', 'info');
      };

      wsRef.current.onmessage = event => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'monitoring_update') {
            setMonitoringData(data.data);
            setLastRefresh(new Date());
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      wsRef.current.onclose = () => {
        console.log('🔌 WebSocket disconnected');
        setWsConnected(false);
        addNotification('WebSocket disconnected - attempting to reconnect...', 'warning');
        // Attempt to reconnect after 5 seconds
        setTimeout(setupWebSocket, 5000);
      };

      wsRef.current.onerror = error => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
    } catch (err) {
      console.error('Error setting up WebSocket:', err);
      setWsConnected(false);
    }
  };

  useEffect(() => {
    fetchMonitoringData();
    setupWebSocket();

    // Set up periodic refresh every 60 seconds
    intervalRef.current = setInterval(() => {
      fetchMonitoringData();
    }, 60000) as unknown as number;

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-slate-400'>Loading monitoring data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='space-y-4'>
        <Card title='Error' subtitle='Failed to load monitoring data'>
          <div className='text-rose-400 mb-3'>{error}</div>
          <Button onClick={() => fetchMonitoringData(true)} variant='soft'>
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      {/* Notifications */}
      {notifications.length > 0 && (
        <div className='space-y-2'>
          {notifications.map(notification => (
            <div
              key={notification.id}
              className={`p-3 rounded-lg border ${
                notification.type === 'error'
                  ? 'bg-red-500/10 border-red-500/20 text-red-400'
                  : notification.type === 'warning'
                    ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400'
                    : notification.type === 'success'
                      ? 'bg-green-500/10 border-green-500/20 text-green-400'
                      : 'bg-blue-500/10 border-blue-500/20 text-blue-400'
              }`}
            >
              {notification.message}
            </div>
          ))}
        </div>
      )}

      {/* SFTP Server Monitoring */}
      <Card title='SFTP Server Monitoring' subtitle='Real-time SFTP server metrics and activity'>
        {monitoringData?.sftpMetrics ? (
          <div className='space-y-6'>
            {/* Server Info */}
            <div className='grid grid-cols-12 gap-4'>
              <div className='col-span-12 sm:col-span-6'>
                <div className='p-4 bg-slate-800/50 rounded-lg'>
                  <div className='flex items-center gap-2 mb-2'>
                    <Server className='w-4 h-4 text-blue-400' />
                    <span className='text-sm font-medium text-slate-300'>Server ID</span>
                  </div>
                  <div className='text-lg font-mono text-white'>
                    {monitoringData.sftpMetrics.serverId}
                  </div>
                </div>
              </div>
              <div className='col-span-12 sm:col-span-6'>
                <div className='p-4 bg-slate-800/50 rounded-lg'>
                  <div className='flex items-center gap-2 mb-2'>
                    <Activity className='w-4 h-4 text-green-400' />
                    <span className='text-sm font-medium text-slate-300'>Monitoring Period</span>
                  </div>
                  <div className='text-lg text-white'>{monitoringData.sftpMetrics.period}</div>
                </div>
              </div>
            </div>

            {/* Metrics Summary */}
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
              <div className='p-4 bg-slate-800/50 rounded-lg'>
                <div className='text-sm text-slate-400 mb-1'>Data In</div>
                <div className='text-2xl font-bold text-blue-400'>
                  {formatBytes(monitoringData.sftpMetrics.metrics.BytesIn.summary.total)}
                </div>
                <div className='text-xs text-slate-500'>
                  Avg: {formatBytes(monitoringData.sftpMetrics.metrics.BytesIn.summary.average)}
                </div>
              </div>
              <div className='p-4 bg-slate-800/50 rounded-lg'>
                <div className='text-sm text-slate-400 mb-1'>Data Out</div>
                <div className='text-2xl font-bold text-green-400'>
                  {formatBytes(monitoringData.sftpMetrics.metrics.BytesOut.summary.total)}
                </div>
                <div className='text-xs text-slate-500'>
                  Avg: {formatBytes(monitoringData.sftpMetrics.metrics.BytesOut.summary.average)}
                </div>
              </div>
              <div className='p-4 bg-slate-800/50 rounded-lg'>
                <div className='text-sm text-slate-400 mb-1'>Files In</div>
                <div className='text-2xl font-bold text-purple-400'>
                  {monitoringData.sftpMetrics.metrics.FilesIn.summary.total.toLocaleString()}
                </div>
                <div className='text-xs text-slate-500'>
                  Avg: {monitoringData.sftpMetrics.metrics.FilesIn.summary.average.toFixed(1)}
                </div>
              </div>
              <div className='p-4 bg-slate-800/50 rounded-lg'>
                <div className='text-sm text-slate-400 mb-1'>Files Out</div>
                <div className='text-2xl font-bold text-orange-400'>
                  {monitoringData.sftpMetrics.metrics.FilesOut.summary.total.toLocaleString()}
                </div>
                <div className='text-xs text-slate-500'>
                  Avg: {monitoringData.sftpMetrics.metrics.FilesOut.summary.average.toFixed(1)}
                </div>
              </div>
            </div>

            {/* Transfer Activity Chart */}
            <div className='mt-6'>
              <SftpMonitoringChart
                sftpMetrics={{
                  serverId: monitoringData.sftpMetrics.serverId,
                  period: monitoringData.sftpMetrics.period,
                  lastUpdated: monitoringData.sftpMetrics.lastUpdated,
                  note: monitoringData.sftpMetrics.note,
                  metrics: monitoringData.sftpMetrics.metrics,
                }}
              />
            </div>
          </div>
        ) : (
          <div className='text-center py-8 text-slate-400'>
            <Server className='w-12 h-12 mx-auto mb-4 opacity-50' />
            <div>No SFTP monitoring data available</div>
          </div>
        )}
      </Card>

      {/* Failed Login Attempts - Detailed List */}
      <Card title='Failed Login Attempts' subtitle='Recent authentication failures with user details'>
        {monitoringData?.failedLogins && monitoringData.failedLogins.length > 0 ? (
          <div className='space-y-3 max-h-96 overflow-y-auto'>
            {monitoringData.failedLogins.slice(0, 20).map((login, index) => (
              <div
                key={index}
                className='p-3 rounded-lg border bg-red-500/10 border-red-500/20 hover:bg-red-500/15 transition-colors'
              >
                <div className='flex items-start justify-between mb-2'>
                  <div className='flex items-center gap-2 flex-1'>
                    <UserX className='w-4 h-4 text-red-400 flex-shrink-0' />
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2 flex-wrap'>
                        <span className='font-medium text-white truncate'>
                          {login.username || login.userId || 'Unknown User'}
                        </span>
                        {login.userId && login.userId !== login.username && (
                          <span className='text-xs text-slate-400'>({login.userId})</span>
                        )}
                      </div>
                      {login.reason && (
                        <div className='text-xs text-red-300 mt-1 truncate'>{login.reason}</div>
                      )}
                    </div>
                  </div>
                  <div className='text-right flex-shrink-0 ml-2'>
                    <div className='text-xs text-slate-400'>{formatTimestamp(login.timestamp)}</div>
                    {login.ipAddress && login.ipAddress !== 'unknown' && (
                      <div className='text-xs text-slate-500 mt-1'>{login.ipAddress}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {monitoringData.failedLogins.length > 20 && (
              <div className='text-center text-sm text-slate-400 pt-2'>
                Showing 20 of {monitoringData.failedLogins.length} failed login attempts
              </div>
            )}
          </div>
        ) : (
          <div className='text-center py-8 text-slate-400'>
            <XCircle className='w-12 h-12 mx-auto mb-4 opacity-50' />
            <div>No failed login attempts in the last 7 days</div>
          </div>
        )}
      </Card>

      {/* Error Monitoring Summary */}
      <Card title='Error Monitoring Summary' subtitle='System errors and issues tracking'>
        {monitoringData?.errorCounts ? (
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
            <div className='text-center p-4 bg-slate-800/50 rounded-lg border border-red-500/20'>
              <div className='flex items-center justify-center gap-2 mb-2'>
                <XCircle className='w-5 h-5 text-red-400' />
                <div className='text-2xl font-bold text-red-400'>
                  {monitoringData.errorCounts.failedLogins}
                </div>
              </div>
              <div className='text-sm text-slate-400'>Failed Logins</div>
              <div className='text-xs text-slate-500 mt-1'>
                {monitoringData.failedLogins?.length || 0} detailed records
              </div>
            </div>
            <div className='text-center p-4 bg-slate-800/50 rounded-lg border border-orange-500/20'>
              <div className='flex items-center justify-center gap-2 mb-2'>
                <AlertCircle className='w-5 h-5 text-orange-400' />
                <div className='text-2xl font-bold text-orange-400'>
                  {monitoringData.errorCounts.incompleteTransfers}
                </div>
              </div>
              <div className='text-sm text-slate-400'>Incomplete Transfers</div>
            </div>
            <div className='text-center p-4 bg-slate-800/50 rounded-lg border border-yellow-500/20'>
              <div className='flex items-center justify-center gap-2 mb-2'>
                <AlertTriangle className='w-5 h-5 text-yellow-400' />
                <div className='text-2xl font-bold text-yellow-400'>
                  {monitoringData.errorCounts.s3Errors}
                </div>
              </div>
              <div className='text-sm text-slate-400'>S3 Errors</div>
            </div>
            <div className='text-center p-4 bg-slate-800/50 rounded-lg border border-purple-500/20'>
              <div className='flex items-center justify-center gap-2 mb-2'>
                <AlertCircle className='w-5 h-5 text-purple-400' />
                <div className='text-2xl font-bold text-purple-400'>
                  {monitoringData.errorCounts.apiErrors}
                </div>
              </div>
              <div className='text-sm text-slate-400'>API Errors</div>
            </div>
          </div>
        ) : (
          <div className='text-center py-4 text-slate-400'>No error data available</div>
        )}
      </Card>

      {/* Security Events */}
      <Card title='Recent Security Events' subtitle='Latest security incidents and alerts'>
        {monitoringData?.securityEvents && monitoringData.securityEvents.length > 0 ? (
          <div className='space-y-3 max-h-96 overflow-y-auto'>
            {monitoringData.securityEvents.slice(0, 10).map((event, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border ${
                  event.severity === 'critical'
                    ? 'bg-red-500/10 border-red-500/20'
                    : event.severity === 'high'
                      ? 'bg-orange-500/10 border-orange-500/20'
                      : event.severity === 'medium'
                        ? 'bg-yellow-500/10 border-yellow-500/20'
                        : 'bg-blue-500/10 border-blue-500/20'
                }`}
              >
                <div className='flex items-center justify-between mb-2'>
                  <div className='flex items-center gap-2'>
                    <Shield className='w-4 h-4 text-slate-400' />
                    <span className='font-medium text-white'>{event.type}</span>
                    <span
                      className={`px-2 py-1 text-xs rounded ${
                        event.severity === 'critical'
                          ? 'bg-red-500/20 text-red-400'
                          : event.severity === 'high'
                            ? 'bg-orange-500/20 text-orange-400'
                            : event.severity === 'medium'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-blue-500/20 text-blue-400'
                      }`}
                    >
                      {event.severity.toUpperCase()}
                    </span>
                  </div>
                  <span className='text-xs text-slate-400'>{formatTimestamp(event.timestamp)}</span>
                </div>
                <div className='text-sm text-slate-300'>{event.description}</div>
                {event.userId && (
                  <div className='text-xs text-slate-500 mt-1'>User: {event.userId}</div>
                )}
                {event.ipAddress && (
                  <div className='text-xs text-slate-500'>IP: {event.ipAddress}</div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className='text-center py-4 text-slate-400'>No security events recorded</div>
        )}
      </Card>

      <Copyright />
    </div>
  );
}
